import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Upload, Trash2, Download, Filter, FolderOpen, File, FileCheck, FileWarning } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: "title_deed", label: "Title Deed", icon: FileCheck },
  { value: "agreement", label: "Sale Agreement", icon: FileText },
  { value: "noc", label: "NOC", icon: FileWarning },
  { value: "possession_letter", label: "Possession Letter", icon: File },
  { value: "tax_receipt", label: "Tax Receipt", icon: FileText },
  { value: "id_proof", label: "ID Proof", icon: FileText },
  { value: "income_proof", label: "Income Proof", icon: FileText },
  { value: "bank_statement", label: "Bank Statement", icon: FileText },
  { value: "other", label: "Other Document", icon: FolderOpen },
];

export default function Documents() {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("property_id");
  const leadId = searchParams.get("lead_id");

  const [documents, setDocuments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterType, setFilterType] = useState("all");

  // Get current user info for filtering
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user.role === "admin";
  const isTeamLeader = user.role === "team_leader";
  const canSeeAllProperties = isAdmin || isTeamLeader;

  const [formData, setFormData] = useState({
    name: "",
    document_type: "other",
    description: "",
    property_id: propertyId || "",
    lead_id: leadId || "",
    file: null,
  });

  useEffect(() => {
    fetchData();
  }, [propertyId, leadId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Build query params
      let query = "";
      if (propertyId) query = `?property_id=${propertyId}`;
      else if (leadId) query = `?lead_id=${leadId}`;

      const [docsRes, propsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/documents${query}`, { headers }),
        axios.get(`${API}/properties`, { headers }),
        axios.get(`${API}/leads`, { headers }),
      ]);

      setDocuments(docsRes.data);
      
      // Filter properties to only show user's own properties for non-admin/non-team-leader
      let filteredProperties = propsRes.data;
      if (!canSeeAllProperties && user.id) {
        filteredProperties = propsRes.data.filter(prop => prop.created_by === user.id);
      }
      setProperties(filteredProperties);
      
      setLeads(leadsRes.data);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, ""),
      }));
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast.error("Please select a file to upload");
      return;
    }

    setUploading(true);

    try {
      const token = localStorage.getItem("token");

      // First, upload file to Cloudinary
      const cloudinaryFormData = new FormData();
      cloudinaryFormData.append("file", formData.file);

      const uploadRes = await axios.post(`${API}/upload`, cloudinaryFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      const fileUrl = uploadRes.data.url;

      // Then create document record
      const docData = {
        name: formData.name,
        document_type: formData.document_type,
        description: formData.description,
        property_id: formData.property_id || null,
        lead_id: formData.lead_id || null,
        file_url: fileUrl,
      };

      await axios.post(`${API}/documents`, docData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Document uploaded successfully");
      setShowUploadDialog(false);
      setFormData({
        name: "",
        document_type: "other",
        description: "",
        property_id: propertyId || "",
        lead_id: leadId || "",
        file: null,
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/documents/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Document deleted successfully");
      setDocuments(documents.filter((d) => d.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getDocTypeInfo = (type) => {
    return DOCUMENT_TYPES.find((t) => t.value === type) || DOCUMENT_TYPES[DOCUMENT_TYPES.length - 1];
  };

  const getPropertyName = (propId) => {
    const property = properties.find((p) => p.id === propId);
    return property ? property.title : "—";
  };

  const getLeadName = (leadIdVal) => {
    const lead = leads.find((l) => l.id === leadIdVal);
    return lead ? lead.name : "—";
  };

  const filteredDocuments = filterType === "all"
    ? documents
    : documents.filter((d) => d.document_type === filterType);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Document Management
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {propertyId ? "Property Documents" : leadId ? "Lead Documents" : "All Documents"}
              </p>
            </div>
            <Button
              data-testid="upload-document-btn"
              onClick={() => setShowUploadDialog(true)}
              className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
            >
              <Upload className="h-5 w-5 mr-2" />
              Upload Document
            </Button>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="h-10 rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                data-testid="filter-type-select"
              >
                <option value="all">All Types</option>
                {DOCUMENT_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <span className="text-sm text-slate-500">
              {filteredDocuments.length} document{filteredDocuments.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Documents Grid */}
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-md border border-slate-200">
            <FolderOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">No documents found</p>
            <p className="text-slate-400 text-sm mt-2">Upload your first document to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredDocuments.map((doc) => {
              const typeInfo = getDocTypeInfo(doc.document_type);
              const TypeIcon = typeInfo.icon;

              return (
                <div
                  key={doc.id}
                  data-testid={`document-card-${doc.id}`}
                  className="bg-white rounded-md border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-12 w-12 rounded-lg bg-blue-900/10 flex items-center justify-center">
                      <TypeIcon className="h-6 w-6 text-blue-900" />
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <Download className="h-4 w-4 text-slate-600" />
                      </a>
                      <button
                        onClick={() => setDeleteId(doc.id)}
                        className="p-2 hover:bg-[#0f2744]/10 rounded-sm transition-colors"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-[#0f2744]" />
                      </button>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-1 truncate" title={doc.name}>
                    {doc.name}
                  </h3>
                  <span className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded uppercase font-medium">
                    {typeInfo.label}
                  </span>

                  {doc.description && (
                    <p className="text-sm text-slate-600 mt-3 line-clamp-2">{doc.description}</p>
                  )}

                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-1">
                    {doc.property_id && (
                      <p className="text-xs text-slate-500">
                        <span className="font-medium">Property:</span> {getPropertyName(doc.property_id)}
                      </p>
                    )}
                    {doc.lead_id && (
                      <p className="text-xs text-slate-500">
                        <span className="font-medium">Lead:</span> {getLeadName(doc.lead_id)}
                      </p>
                    )}
                    <p className="text-xs text-slate-400">
                      Uploaded {new Date(doc.created_at).toLocaleDateString("en-IN")}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader className="bg-gradient-to-r from-[#c9a227] to-[#d4af37] -mx-6 -mt-6 px-6 py-4 rounded-t-lg">
              <DialogTitle className="text-white">Upload Document</DialogTitle>
              <DialogDescription className="text-white/80">
                Upload a document and link it to a property or lead.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleUpload} className="space-y-4 pt-2">
              <div>
                <Label htmlFor="file">Select File *</Label>
                <Input
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  className="mt-1 h-11 rounded-sm cursor-pointer"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                  data-testid="file-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Supported: PDF, DOC, DOCX, JPG, PNG, XLS, XLSX
                </p>
              </div>

              <div>
                <Label htmlFor="name">Document Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 h-11 rounded-sm"
                  placeholder="Sale Agreement - Plot 123"
                  required
                  data-testid="document-name-input"
                />
              </div>

              <div>
                <Label htmlFor="document_type">Document Type *</Label>
                <select
                  id="document_type"
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleChange}
                  className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900"
                  data-testid="document-type-select"
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:border-blue-900 focus:ring-1 focus:ring-blue-900 resize-none"
                  rows={2}
                  placeholder="Optional notes about this document..."
                  data-testid="document-description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="property_id">
                    Link to Property {!canSeeAllProperties && "(Your Properties Only)"}
                  </Label>
                  <select
                    id="property_id"
                    name="property_id"
                    value={formData.property_id}
                    onChange={handleChange}
                    className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a227]"
                    data-testid="link-property-select"
                  >
                    <option value="">None</option>
                    {properties.length === 0 ? (
                      <option value="" disabled>No properties available</option>
                    ) : (
                      properties.map((prop) => (
                        <option key={prop.id} value={prop.id}>
                          {prop.title}
                        </option>
                      ))
                    )}
                  </select>
                  {!canSeeAllProperties && properties.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">You haven't added any properties yet</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="lead_id">Link to Lead</Label>
                  <select
                    id="lead_id"
                    name="lead_id"
                    value={formData.lead_id}
                    onChange={handleChange}
                    className="mt-1 h-11 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm focus:border-[#c9a227] focus:ring-1 focus:ring-[#c9a227]"
                    data-testid="link-lead-select"
                  >
                    <option value="">None</option>
                    {leads.map((lead) => (
                      <option key={lead.id} value={lead.id}>
                        {lead.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowUploadDialog(false)}
                  className="rounded-sm"
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-900 hover:bg-blue-800 text-white rounded-sm"
                  disabled={uploading}
                  data-testid="submit-upload-btn"
                >
                  {uploading ? "Uploading..." : "Upload Document"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Document</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this document? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-[#0f2744] hover:bg-[#1e3a5f]">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
